"use client"

import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react"
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import type { DialogRootChangeEventReason } from "@base-ui/react/dialog"
import type { DrawerRootChangeEventReason } from "@base-ui/react/drawer"
import { cn } from "cn"

import {
  AmplitudeDialogAnalyticsName,
  AmplitudeDialogName,
  AmplitudeEmptyValue,
} from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { selectIsSplitView, selectIsXS } from "@/lib/storage/app-slice"
import { setDialogClosed, setDialogOpened } from "@/lib/storage/dialog-slice"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { assertPresent } from "@/lib/utils/assertions"
import {
  claimPendingDialogKeyboard,
  hasTransientUserActivation,
  summonKeyboard,
  type KeyboardHandOff,
} from "@/lib/utils/dom"
import { present } from "@/lib/utils/presence"
import IconCross from "@/components/icons/cross"
import { ButtonProps } from "./button"
import {
  DialogPresentationContext,
  getAutofocusField,
  useDialogPresentation,
  type DialogPresentation,
} from "./dialog-presentation"
import {
  createDrawerHandle,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  type DrawerHandle,
} from "./drawer"
import { IconButton } from "./icon-button"
import { ScrollArea } from "./scroll-area"

type ContentHandlers = {
  onOpen: (handOff: KeyboardHandOff | null) => void
  onClose: () => void
}

type DialogContentCtx = {
  analyticsName?: AmplitudeDialogAnalyticsName
  analyticsProperties?: Record<string, unknown>
  analyticsEnabled?: boolean
  /** Registers open/close handlers; returns an unregister cleanup. */
  registerContent?: (handlers: ContentHandlers) => () => void
} | null

const DialogContentContext = createContext<DialogContentCtx>(null)

function useDialogContentContext() {
  return useContext(DialogContentContext)
}

// Base UI brands DrawerHandle as a subtype of DialogHandle. Since this Dialog
// presents as a Drawer at XS, its handle must be a drawer handle — it is valid
// for both primitives (DrawerHandle extends DialogHandle with no runtime
// changes).
export type DialogHandle = DrawerHandle

export type DialogCloseReason =
  | DialogRootChangeEventReason
  | DrawerRootChangeEventReason

export type DialogChangeEventDetails = {
  reason: DialogCloseReason
  /** Stops Base UI from handling the close. Required for `close-watcher`: returning early leaves the dialog open but destroys the watcher, so the next Android back leaves the page. */
  cancel: () => void
  /** False when the browser replaced `cancel` with a no-op (only one prevented close per gesture). */
  isCanceled: boolean
}

const IMPLICIT_DISMISS_REASONS = new Set<DialogCloseReason>([
  "escape-key",
  "outside-press",
  "close-watcher",
])

/**
 * Blocks escape, outside press, and the Android back gesture. Returns whether
 * the close was actually canceled — a non-cancelable `close-watcher` must
 * still close, matching a native dialog.
 */
export function blockImplicitDismiss(
  nextOpen: boolean,
  event: DialogChangeEventDetails
) {
  if (nextOpen || !IMPLICIT_DISMISS_REASONS.has(event.reason)) return false
  event.cancel()
  return event.isCanceled
}

export type DialogRootProps = Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
  "onOpenChange" | "handle"
> & {
  /** Create with `createDialogHandle()` — usable in both presentations. */
  handle?: DialogHandle
  analyticsName?: AmplitudeDialogAnalyticsName
  analyticsProperties?: Record<string, unknown>
  analyticsEnabled?: boolean
  /** When presented as a Drawer (XS), whether to show the swipe handle. @default true */
  showSwipeHandle?: boolean
  /**
   * `"adaptive"` (default): Drawer below `sm`, Dialog at `sm+`.
   * `"adaptive-split"`: Drawer outside split view, Dialog in split view.
   * `"dialog"`: always Dialog — use when nesting under Sheet/Dialog so Base UI
   * modality (inert layers) stays correct.
   */
  presentation?: "adaptive" | "adaptive-split" | "dialog"
  /**
   * Bring up the iOS virtual keyboard during the tap that opens this dialog so
   * the `autoFocus` text field receives it once the drawer has settled. iOS only
   * shows the keyboard for `focus()` calls inside a user gesture, and a drawer's
   * content mounts and animates in after the tap — too late on its own. Set on
   * dialogs whose primary action is typing. Only applies at XS (drawer
   * presentation); other viewports focus the field directly.
   */
  summonKeyboard?: boolean
  onOpenChange?: (open: boolean, eventDetails: DialogChangeEventDetails) => void
}

/**
 * Wrap Base UI Root so we can provide an analytics name/properties
 * that DialogContent (and other subcomponents) can consume.
 * At XS, presents as Drawer; at sm+ as a centered Dialog.
 */
const Dialog = ({
  analyticsName,
  analyticsProperties,
  analyticsEnabled = true,
  showSwipeHandle = true,
  presentation: presentationProp = "adaptive",
  summonKeyboard: summonKeyboardProp = false,
  children,
  onOpenChange,
  onOpenChangeComplete,
  open,
  defaultOpen,
  ...rest
}: DialogRootProps) => {
  const dispatch = useAppDispatch()
  const isXS = useAppSelector(selectIsXS)
  const isSplitView = useAppSelector(selectIsSplitView)
  const presentsAsDrawer =
    presentationProp === "adaptive-split" ? !isSplitView : isXS
  const presentation: DialogPresentation =
    presentationProp !== "dialog" && presentsAsDrawer ? "drawer" : "dialog"
  const handlersRef = useRef<ContentHandlers | null>(null)
  // Pending keyboard hand-off while the drawer animates in; consumed (or
  // released) in handleOpenChangeComplete.
  const keyboardHandOffRef = useRef<KeyboardHandOff | null>(null)
  // True between open-complete and close-complete; lets fields that mount
  // into an already-open drawer focus themselves (useDialogAutoFocus).
  const settledRef = useRef(false)
  const shouldSummonKeyboard = summonKeyboardProp && presentation === "drawer"

  const releaseKeyboardSummon = () => {
    keyboardHandOffRef.current?.(null)
    keyboardHandOffRef.current = null
  }

  // Handlers live in DialogContent, which mounts lazily and may re-render
  // independently. It registers them here so handleOpenChangeComplete can
  // call the latest versions. Mutation happens in the component that owns
  // the ref (react-hooks/immutability).
  const registerContent = (handlers: ContentHandlers) => {
    handlersRef.current = handlers
    return () => {
      handlersRef.current = null
    }
  }

  // LazyDialog mounts with open={true}, which skips Drawer's enter transition
  // (no closed → open). Defer controlled open by a frame so it animates in.
  const [drawerOpen, setDrawerOpen] = useState(false)
  useIsomorphicLayoutEffect(() => {
    if (presentation !== "drawer" || open === undefined) return
    if (!open) {
      setDrawerOpen(false)
      return
    }
    let cancelled = false
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setDrawerOpen(true)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [open, presentation])

  // The state update that flips `open` is flushed in a microtask of the tap's
  // event dispatch, which WebKit still treats as the gesture, so arming here
  // in the commit is early enough — unlike the drawer's deferred mount and
  // enter transition. A dialog whose chunk loaded after the tap (LazyDialog)
  // is too late to summon; it adopts the keyboard LazyDialog summoned on its
  // behalf instead.
  const wasOpenRef = useRef(false)
  useIsomorphicLayoutEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = !!open
    if (open === false) {
      // Closing. Content that unmounts on close (`{open && <DialogContent/>}`)
      // leaves Base UI no popup to observe, so close-complete never fires;
      // reset here or the next open would focus its field on mount, before
      // the drawer is on screen.
      settledRef.current = false
      releaseKeyboardSummon()
      return
    }
    if (!open || wasOpen) return

    const pending = claimPendingDialogKeyboard()
    if (!shouldSummonKeyboard) {
      pending?.(null)
      return
    }
    if (pending) {
      keyboardHandOffRef.current = pending
    } else if (hasTransientUserActivation()) {
      // Summon the new stand-in before dropping any previous one: removing a
      // focused input would blur it and dismiss the keyboard.
      const previous = keyboardHandOffRef.current
      keyboardHandOffRef.current = summonKeyboard()
      previous?.(null)
    }
  }, [open, shouldSummonKeyboard])

  const contextValue = {
    analyticsName,
    analyticsProperties,
    analyticsEnabled,
    registerContent,
  }

  function handleOpenChangeComplete(isOpen: boolean) {
    settledRef.current = isOpen
    if (isOpen) {
      dispatch(setDialogOpened())
      const handOff = keyboardHandOffRef.current
      keyboardHandOffRef.current = null
      if (handlersRef.current) {
        handlersRef.current.onOpen(handOff)
      } else {
        handOff?.(null)
      }
    } else {
      dispatch(setDialogClosed())
      handlersRef.current?.onClose()
      releaseKeyboardSummon()
    }
    onOpenChangeComplete?.(isOpen)
  }

  // Close applies immediately; open is deferred via drawerOpen for enter anim.
  const resolvedDrawerOpen =
    open === undefined ? undefined : open ? drawerOpen : false

  const root =
    presentation === "drawer" ? (
      <Drawer
        showSwipeHandle={showSwipeHandle}
        open={resolvedDrawerOpen}
        defaultOpen={defaultOpen}
        onOpenChange={
          onOpenChange as ComponentPropsWithoutRef<
            typeof Drawer
          >["onOpenChange"]
        }
        onOpenChangeComplete={handleOpenChangeComplete}
        {...rest}
      >
        {children}
      </Drawer>
    ) : (
      <DialogPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={
          onOpenChange as DialogPrimitive.Root.Props["onOpenChange"]
        }
        onOpenChangeComplete={handleOpenChangeComplete}
        {...rest}
      >
        {children}
      </DialogPrimitive.Root>
    )

  return (
    <DialogPresentationContext.Provider
      value={{
        presentation,
        showSwipeHandle,
        isSettled: () => settledRef.current,
      }}
    >
      <DialogContentContext.Provider value={contextValue}>
        {root}
      </DialogContentContext.Provider>
    </DialogPresentationContext.Provider>
  )
}

function DialogTrigger({
  ...props
}: Omit<DialogPrimitive.Trigger.Props, "handle"> & {
  handle?: DialogHandle
}) {
  const { presentation } = useDialogPresentation()
  if (presentation === "drawer") {
    return <DrawerTrigger {...props} />
  }
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  const { presentation } = useDialogPresentation()
  if (presentation === "drawer") {
    return <DrawerClose {...props} />
  }
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogCloseButton({
  variant = "ghost",
  className,
}: {
  variant?: ButtonProps["variant"]
  className?: string
}) {
  return (
    <DialogClose
      render={
        <IconButton
          variant={variant}
          icon={{
            icon: IconCross,
            className: "text-[1.125em]",
            strokeWidth: 3,
          }}
          label="Close"
          className={className}
        />
      }
    />
  )
}

function DialogOverlay({
  className,
  forceRender = true,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      forceRender={forceRender}
      className={cn(
        "fixed inset-0 isolate z-overlay bg-black-a-7 backdrop-grayscale-50 duration-dialog data-closed:animate-out data-closed:fade-out data-open:animate-in data-open:fade-in dark:bg-black-a-9 dark:backdrop-blur-2xs",
        className
      )}
      {...props}
    />
  )
}

export interface DialogContentProps extends ComponentPropsWithoutRef<
  typeof DialogPrimitive.Popup
> {
  size?: "sm" | "md" | "lg" | "xl" | "full" | "auto" | number
  hideClose?: boolean
  closeVariant?: ButtonProps["variant"]
  portalContainer?: HTMLElement | null
  backgrounded?: boolean
}

function useDialogContentLifecycle(
  innerRef: RefObject<HTMLDivElement | null>,
  {
    presentation,
    hasExplicitInitialFocus,
  }: {
    presentation: DialogPresentation
    hasExplicitInitialFocus: boolean
  }
) {
  const contentContext = useDialogContentContext()
  const analyticsSnapshotRef = useRef({
    title: AmplitudeEmptyValue,
    subTitle: AmplitudeEmptyValue,
  })

  const registerContent = contentContext?.registerContent
  const analyticsName = contentContext?.analyticsName
  const analyticsProperties = contentContext?.analyticsProperties
  const analyticsEnabled = contentContext?.analyticsEnabled

  useEffect(() => {
    const refreshAnalyticsSnapshot = () => {
      const root = innerRef.current
      if (!root) return
      const title = root
        .querySelector<HTMLElement>("[data-analytics-title]")
        ?.textContent?.trim()
      const subTitle = root
        .querySelector<HTMLElement>("[data-analytics-sub-title]")
        ?.textContent?.trim()
      analyticsSnapshotRef.current = {
        title: present(title) ? title : AmplitudeEmptyValue,
        subTitle: present(subTitle) ? subTitle : AmplitudeEmptyValue,
      }
    }

    const sendDialogAnalytics = (action: "Open" | "Close") => {
      if (!analyticsEnabled) return
      if (action === "Open") refreshAnalyticsSnapshot()

      const eventName = analyticsName
        ? (`Dialog${analyticsName}${action}` as AmplitudeDialogName)
        : (`Dialog${action}` as AmplitudeDialogName)
      const dialogAnalyticsProperties = {
        dialog: {
          title: analyticsSnapshotRef.current.title,
          subTitle: analyticsSnapshotRef.current.subTitle,
          context: AmplitudeEmptyValue,
        },
      }
      const { title, subTitle, context } = analyticsProperties ?? {}
      if (typeof title === "string") {
        dialogAnalyticsProperties.dialog.title = title
      }
      if (typeof subTitle === "string") {
        dialogAnalyticsProperties.dialog.subTitle = subTitle
      }
      if (typeof context === "string") {
        dialogAnalyticsProperties.dialog.context = context
      }
      sendAnalyticsEvent({
        name: eventName,
        properties: dialogAnalyticsProperties,
      })
    }

    const handleOpen = (handOff: KeyboardHandOff | null) => {
      // The drawer has finished sliding in, so the field is at its final
      // position: focusing it now does not make iOS scroll to reveal it.
      const field = hasExplicitInitialFocus
        ? null
        : getAutofocusField(innerRef.current)
      if (handOff) {
        handOff(field)
      } else if (presentation === "drawer") {
        field?.focus()
      }
      sendDialogAnalytics("Open")
    }

    return registerContent?.({
      onOpen: handleOpen,
      onClose: () => sendDialogAnalytics("Close"),
    })
  }, [
    analyticsEnabled,
    analyticsName,
    analyticsProperties,
    hasExplicitInitialFocus,
    innerRef,
    presentation,
    registerContent,
  ])
}

const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Popup>,
  DialogContentProps
>(
  (
    {
      size = "md",
      hideClose,
      closeVariant = "ghost",
      portalContainer,
      backgrounded,
      className,
      children,
      initialFocus: initialFocusProp,
      ...props
    },
    ref
  ) => {
    const { presentation, showSwipeHandle } = useDialogPresentation()
    const innerRef = useRef<HTMLDivElement>(null)
    const defaultPortalContainer =
      typeof document !== "undefined" ? document.body : undefined

    const hasExplicitInitialFocus = initialFocusProp !== undefined
    useDialogContentLifecycle(innerRef, {
      presentation,
      hasExplicitInitialFocus,
    })

    // Base UI resolves this after the popup mounts, so the content is
    // queryable here even though it was not when the dialog was closed.
    const defaultInitialFocus = useCallback(() => {
      const field = getAutofocusField(innerRef.current)
      if (presentation === "drawer") {
        // With a field to focus, do nothing during the enter transition: the
        // root focuses it once the drawer has settled, and moving focus now
        // would blur the summoned keyboard's stand-in input and dismiss it.
        // Otherwise fall back to Base UI's default (first tabbable, else popup).
        return field ? false : true
      }
      return field ?? innerRef.current ?? true
    }, [presentation])
    const initialFocus = initialFocusProp ?? defaultInitialFocus

    // Drawer with a swipe handle already affords dismiss — skip the dialog X.
    // If the handle is hidden, keep the close button unless hideClose is set.
    const showCloseButton =
      !hideClose && (presentation === "dialog" || !showSwipeHandle)

    const closeButton = showCloseButton && (
      <div className="absolute top-2 left-(--dialog-pad) -ml-2">
        <DialogCloseButton variant={closeVariant} />
      </div>
    )

    if (presentation === "drawer") {
      return (
        <DrawerContent
          size={size === "full" ? "full" : "default"}
          className={cn(
            backgrounded && "pointer-events-none opacity-0",
            className
          )}
          overlayClassName={
            backgrounded
              ? "pointer-events-none opacity-0 transition-opacity"
              : undefined
          }
          initialFocus={initialFocus}
        >
          <div ref={innerRef} className="contents">
            <div className="contents">{children}</div>
            {closeButton}
          </div>
        </DrawerContent>
      )
    }

    return (
      <DialogPrimitive.Portal
        container={portalContainer ?? defaultPortalContainer}
      >
        <DialogOverlay
          className={
            backgrounded
              ? "pointer-events-none opacity-0 transition-opacity"
              : undefined
          }
        />
        <DialogPrimitive.Popup
          ref={ref}
          aria-describedby={undefined}
          initialFocus={initialFocus}
          className="pointer-events-none! fixed inset-0 z-overlay flex animate-in items-end justify-center data-ending-style:animate-out sm:items-center"
          {...props}
        >
          <div
            className={cn(
              "flex max-h-full w-full pt-(--header-height) sm:py-4",
              size === "sm" && "sm:max-w-sm",
              size === "md" && "sm:max-w-lg",
              size === "lg" && "sm:max-w-xl",
              size === "xl" && "sm:max-w-3xl",
              size === "full" && "h-full py-0!",
              size === "auto" && "sm:max-w-fit"
            )}
            style={{
              maxWidth: typeof size === "number" ? `${size}px` : undefined,
            }}
          >
            <div
              ref={innerRef}
              tabIndex={-1}
              data-slot="dialog-content"
              className={cn(
                "pointer-events-auto relative flex w-full flex-col overflow-hidden bg-background shadow-ring-xl outline-hidden transition-[opacity,transform] duration-drawer ease-drawer in-data-closed:animate-out in-data-closed:fade-out in-data-closed:slide-out-to-bottom in-data-open:animate-in in-data-open:fade-in in-data-open:slide-in-from-bottom sm:duration-dialog sm:ease-in-out sm:in-data-closed:slide-out-to-top-4 sm:in-data-open:slide-in-from-top-4 where:rounded-t-2xl where:sm:rounded-2xl",
                backgrounded && "pointer-events-none opacity-0",
                size === "full" && "min-h-svh rounded-none",
                className
              )}
            >
              <div className="contents">{children}</div>
              {closeButton}
            </div>
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    )
  }
)

DialogContent.displayName = "DialogContent"

function DialogHeader({
  className,
  separator = true,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  separator?: boolean
}) {
  const { presentation } = useDialogPresentation()

  if (presentation === "drawer") {
    return (
      <DrawerHeader
        className={cn(separator && "border-b border-separator pb-4", className)}
        {...props}
      >
        {children}
      </DrawerHeader>
    )
  }

  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex-none px-[calc(var(--dialog-pad)+(var(--spacing)*7))] pt-(--dialog-pad) text-center **:data-[slot=dialog-description]:-mx-7",
        separator && "border-b border-separator pb-4",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
DialogHeader.displayName = "DialogHeader"

function DialogTitle({
  className,
  size = 2,
  unstyled,
  ...props
}: DialogPrimitive.Title.Props & {
  size?: 1 | 2
  unstyled?: boolean
}) {
  const { presentation } = useDialogPresentation()
  const titleClassName = cn(
    !unstyled && [
      "line-clamp-2 font-semibold text-balance wrap-anywhere",
      size === 1 && "text-base leading-snug",
      size === 2 && "text-lg leading-snug sm:text-xl",
    ],
    className
  )

  if (presentation === "drawer") {
    return (
      <DrawerTitle
        data-analytics-title=""
        className={titleClassName}
        {...props}
      />
    )
  }

  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      data-analytics-title=""
      className={titleClassName}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  muted = true,
  ...props
}: DialogPrimitive.Description.Props & {
  muted?: boolean
}) {
  const { presentation } = useDialogPresentation()
  const descriptionClassName = cn(
    "mt-1 text-xs text-pretty wrap-anywhere",
    muted && "text-muted",
    className
  )

  if (presentation === "drawer") {
    return (
      <DrawerDescription
        data-analytics-sub-title=""
        className={descriptionClassName}
        {...props}
      />
    )
  }

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      data-analytics-sub-title=""
      className={descriptionClassName}
      {...props}
    />
  )
}

function hasRenderableChildren(children: ReactNode) {
  return Children.toArray(children).some((child) => child != null)
}

const DialogBody = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    scrollAreaClassName?: string
  }
>(({ className, scrollAreaClassName, children, ...props }, externalRef) => {
  const { presentation } = useDialogPresentation()
  const internalRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(externalRef, () => assertPresent(internalRef.current), [])
  const [hasScroll, setHasScroll] = useState(false)

  useEffect(() => {
    const checkScroll = () => {
      if (internalRef.current) {
        setHasScroll(
          internalRef.current.scrollHeight > internalRef.current.clientHeight
        )
      }
    }
    checkScroll()
    const resizeObserver = new ResizeObserver(() => {
      checkScroll()
    })
    const mutationObserver = new MutationObserver(() => {
      checkScroll()
    })

    if (internalRef.current) {
      resizeObserver.observe(internalRef.current)
      mutationObserver.observe(internalRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  if (!hasRenderableChildren(children)) {
    return null
  }

  if (presentation === "drawer") {
    return (
      <DrawerBody
        ref={internalRef}
        className={className}
        data-has-scroll={hasScroll}
        {...props}
      >
        {children}
      </DrawerBody>
    )
  }

  return (
    <ScrollArea
      ref={internalRef}
      data-slot="dialog-body"
      data-has-scroll={hasScroll}
      className={cn("flex flex-col", scrollAreaClassName)}
      contentClassName={cn(
        "where:p-(--dialog-pad) [[data-slot=dialog-body]:last-child_&]:where:pb-9",
        className
      )}
      {...props}
    >
      {children}
    </ScrollArea>
  )
})
DialogBody.displayName = "DialogBody"

const FOOTER_SHADOW_CLASSNAME =
  "relative z-1 overflow-hidden rounded-t-2xl [[data-has-scroll=true]+&]:shadow-xl dark:[[data-has-scroll=true]+&]:shadow-ring-xl"

function DialogFooter({
  className,
  shadow = true,
  separator,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  separator?: boolean
  shadow?: boolean
}) {
  const { presentation } = useDialogPresentation()

  if (presentation === "drawer") {
    return (
      <DrawerFooter
        className={cn(
          "flex-col-reverse",
          shadow && FOOTER_SHADOW_CLASSNAME,
          separator && "border-t shadow-none!",
          className
        )}
        {...props}
      />
    )
  }

  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 flex-col-reverse gap-2 px-(--dialog-pad) pt-4 pb-(--dialog-pad) sm:flex-row sm:justify-end",
        shadow && FOOTER_SHADOW_CLASSNAME,
        separator && "border-t shadow-none!",
        className
      )}
      {...props}
    />
  )
}
DialogFooter.displayName = "DialogFooter"

const createDialogHandle = createDrawerHandle

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
  createDialogHandle,
}
